import {
  Alert,
  Box,
  Button,
  ButtonProps,
  Icon,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Typography
} from "@outschool/backpack";
import {
  PauseOngoingEnrollmentsMutation,
  PauseOngoingEnrollmentsMutationVariables,
  PauseOngoingModalBodyQuery,
  PauseOngoingModalBodyQueryVariables
} from "@outschool/gql-frontend-generated";
import {
  faDiamondExclamation,
  faPiggyBank,
  faUsersClass
} from "@outschool/icons";
import { Trans, useTranslation } from "@outschool/localization";
import {
  briefDurationStringWithoutTimes,
  briefDurationTimesString,
  dayjs,
  formatDateWithWeekday,
  startOfIsoWeekUtc
} from "@outschool/time";
import {
  gql,
  useMutation,
  useQueryWithPreviousData
} from "@outschool/ui-apollo";
import { useTopNoticeContext } from "@outschool/ui-components-website";
import {
  Checkbox,
  Loading,
  ModalActionButtons
} from "@outschool/ui-legacy-component-library";
import _ from "lodash";
import React, { FormEvent, useMemo, useState } from "react";

import * as Env from "../../../shared/Env";
import { useTimeZone } from "../../providers/TimeZoneProvider";
import { pauseOngoingEnrollmentsMutation } from "../../queries/EnrollmentMutations";
import ActivitySummary from "../enrolling/ActivitySummary";
import ErrorMessage from "../ErrorMessage";
import PageModal from "../PageModal";
import {
  EnrolledClassActivity,
  EnrolledClassEnrollment,
  EnrolledClassGiftEnrollment,
  EnrolledClassSection
} from "./EnrolledClassTypes";

type Meetings = NonNullable<PauseOngoingModalBodyQuery["section"]>["meetings"];

type ParentPauseOngoingButtonProps = ButtonProps & {
  section: EnrolledClassSection;
  activity: EnrolledClassActivity;
  enrollment: EnrolledClassEnrollment | EnrolledClassGiftEnrollment;
  open: boolean;
  setOpen: (a: boolean) => void;
};

interface WeekPeriod {
  weekStart: Date;
  weekEnd: Date;
  meetingsInPeriod: Meetings;
}

const NUM_FUTURE_PAUSABLE_WEEKS = 12;

export default React.forwardRef(_ParentPauseOngoingButton);

function _ParentPauseOngoingButton(
  {
    activity,
    section,
    enrollment,
    onClick,
    open,
    setOpen,
    ...buttonProps
  }: ParentPauseOngoingButtonProps,
  ref: React.Ref<any>
) {
  const { t } = useTranslation(
    "client\\components\\account\\ParentPauseOngoingButton"
  );

  return (
    <>
      <Button
        ref={ref}
        variant="link"
        onClick={(event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
          onClick?.(event);
          setOpen(true);
        }}
        {...buttonProps}
      >
        {enrollment?.pauseWindow ? t`Manage Pause` : t`Pause`}
      </Button>
      <PageModal open={open} onClose={() => setOpen(false)}>
        <PauseOngoingModalBody
          activity={activity}
          section={section}
          enrollment={enrollment}
          open={open}
          setOpen={setOpen}
          close={() => setOpen(false)}
        />
      </PageModal>
    </>
  );
}

/**
 * The Enrolled filter doesn't include meetings that correspond to a pause window
 * We need *all* meetings to check if we need to disable the current week or not
 */
const pauseOngoingModalBodyQuery = gql`
  query PauseOngoingModalBody($sectionUid: ID!, $startAfter: DateTime!) {
    section(uid: $sectionUid) {
      uid
      meetings(startAfter: $startAfter) {
        uid
        start_time
        end_time
      }
    }
  }
`;

function PauseOngoingModalBody({
  activity,
  section,
  enrollment,
  close
}: ParentPauseOngoingButtonProps & { close: () => void }) {
  const { setTopNotice } = useTopNoticeContext();
  const { t } = useTranslation(
    "client\\components\\account\\ParentPauseOngoingButton"
  );
  const { timeZone } = useTimeZone();
  const startAfter = React.useMemo(() => dayjs().utc().toDate(), []);
  const { data, loading } = useQueryWithPreviousData<
    PauseOngoingModalBodyQuery,
    PauseOngoingModalBodyQueryVariables
  >(pauseOngoingModalBodyQuery, {
    variables: {
      sectionUid: section.uid,
      startAfter
    }
  });
  const meetings = data?.section?.meetings ?? [];
  const { isThisWeekDisabled, shouldStartAtNextWeek } = useDisplayRules({
    meetings,
    enrollment
  });
  const startOfThisWeek = startOfIsoWeekUtc();
  const firstVisibleWeek = shouldStartAtNextWeek
    ? startOfIsoWeekUtc(dayjs(startOfThisWeek).add(8, "day"))
    : startOfThisWeek;
  const [selectedWeeks, setSelectedWeeks] = React.useState(() => {
    if (enrollment?.pauseWindow) {
      const pauseStart = dayjs(enrollment?.pauseWindow?.startDate!).utc();

      const selectedWeeksStart = pauseStart.isAfter(firstVisibleWeek)
        ? pauseStart
        : dayjs(firstVisibleWeek);

      return periodToWeeks(
        selectedWeeksStart,
        dayjs(enrollment?.pauseWindow?.endDate!).utc(true)
      );
    }
    return [];
  });

  const futureMeetings = useFutureMeetings(meetings);
  const meetingsPerWeekPeriod = React.useMemo(() => {
    const numWeeksToShow = shouldStartAtNextWeek
      ? NUM_FUTURE_PAUSABLE_WEEKS - 1
      : NUM_FUTURE_PAUSABLE_WEEKS;
    const meetingsPerWeekPeriod: WeekPeriod[] = [];
    let periodStartTime = firstVisibleWeek;
    for (let _i = 0; _i < numWeeksToShow; ++_i) {
      const weekEnd = startOfIsoWeekUtc(dayjs(periodStartTime).add(8, "day"));
      const meetingsInPeriod = futureMeetings.filter(m =>
        dayjs(m.start_time).isBetween(periodStartTime, weekEnd, "ms", "[)")
      );
      meetingsPerWeekPeriod.push({
        weekStart: periodStartTime,
        weekEnd,
        meetingsInPeriod
      });
      periodStartTime = weekEnd;
    }
    return meetingsPerWeekPeriod;
  }, [shouldStartAtNextWeek, firstVisibleWeek, futureMeetings]);

  const {
    pauseStartUtc,
    pauseEndUtc,
    pauseStartLocal,
    pauseEndLocal,
    pausedMeetings,
    isWeekSelectionContinuous
  } = useMemo(() => {
    if (selectedWeeks.length === 0) {
      return { isWeekSelectionContinuous: true, pausedMeetings: [] };
    }

    const { pauseStartUtc, pauseEndUtc } = weeksToPeriod(selectedWeeks);
    const pauseStartLocal = dayjs(pauseStartUtc).tz(timeZone);
    const pauseEndLocal = dayjs(pauseEndUtc).tz(timeZone);
    const pausedMeetings = futureMeetings.filter(m =>
      dayjs(m.start_time).isBetween(pauseStartUtc, pauseEndUtc, "ms", "[)")
    );
    const isWeekSelectionContinuous =
      pauseEndUtc!.diff(pauseStartUtc, "weeks") === selectedWeeks.length;

    return {
      pauseStartUtc,
      pauseEndUtc,
      pauseStartLocal,
      pauseEndLocal,
      pausedMeetings,
      isWeekSelectionContinuous
    };
  }, [selectedWeeks, timeZone, futureMeetings]);

  const [pauseOngoingEnrollments, { loading: isPauseRunning }] = useMutation<
    PauseOngoingEnrollmentsMutation,
    PauseOngoingEnrollmentsMutationVariables
  >(pauseOngoingEnrollmentsMutation);

  const [error, setError] = useState("");

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    if (isPauseRunning) {
      return false;
    }
    event.preventDefault();
    setError("");

    try {
      const dates: { startDate: Date; endDate: Date }[] = [];
      if (pauseEndUtc && pauseStartUtc) {
        dates.push({
          startDate: pauseStartUtc.toDate(),
          endDate: pauseEndUtc.toDate()
        });
      }

      const { data } = await pauseOngoingEnrollments({
        variables: {
          input: [
            {
              enrollmentUid: enrollment?.uid!,
              dates
            }
          ]
        }
      });
      if (!data) {
        // Cancel form submission
        return false;
      }
      close();
      setTopNotice(
        t(
          "Your enrollment has been paused from {{startDate}} until {{endDate}}",
          {
            startDate: pauseStartLocal?.format("LLL")!,
            endDate: pauseEndLocal?.format("LLL")
          }
        )
      );
    } catch (e) {
      setError(e);
    }
    // Cancel form submission
    return false;
  };
  const isNewPauseWindowActive = dayjs().isBetween(
    pauseStartUtc,
    pauseEndUtc,
    "ms",
    "[]"
  );

  if (loading) {
    return <Loading />;
  }

  return (
    <>
      <form onSubmit={handleSubmit}>
        <ActivitySummary
          activity={activity}
          startTime={section.start_time}
          endTime={section.end_time}
          nextOngoingMeeting={section.nextOngoingMeeting}
        />

        {enrollment?.learner?.name ? (
          <Typography variant="h3">
            {t("Pause Enrollment for {{learnerName}}", {
              learnerName: enrollment.learner.name
            })}
          </Typography>
        ) : (
          <Typography variant="h3">{t("Pause Enrollment")}</Typography>
        )}
        <ErrorMessage
          value={error}
          showStatusPageLink={Env.IS_READ_ONLY_MODE}
        />
        {enrollment?.isPaused && !isNewPauseWindowActive && (
          <Alert severity="error">
            {t(
              "You will be charged the full weekly enrollment price to attend this week."
            )}
          </Alert>
        )}
        <PauseInstructions />

        <Typography variant="h6" sx={{ marginTop: "1em" }}>
          {t`Pause dates`}
        </Typography>
        <PauseWeekDateTable
          meetingsPerWeekPeriod={meetingsPerWeekPeriod}
          isWeekSelectionContinuous={isWeekSelectionContinuous}
          disableThisWeek={isThisWeekDisabled}
          selectedWeeks={selectedWeeks}
          setSelectedWeeks={setSelectedWeeks}
          timeZone={timeZone}
        />

        <PausedSessionsSummary
          pausedMeetings={pausedMeetings}
          isWeekSelectionContinuous={isWeekSelectionContinuous}
          selectedWeeks={selectedWeeks}
          timeZone={timeZone}
        />
        <Box
          flex
          sx={{
            flexDirection: "row",
            justifyContent: "space-between"
          }}
        >
          <Typography
            sx={{
              paddingTop: "0.5em",
              color: "grey.600"
            }}
          >
            {!!pauseEndLocal &&
              t("Payments will resume on {{endDate}}", {
                endDate: pauseEndLocal.format("ll")
              })}
          </Typography>
          <ModalActionButtons
            onCancel={close}
            disabled={
              isPauseRunning ||
              (selectedWeeks?.length === 0 && !enrollment?.pauseWindow) ||
              !isWeekSelectionContinuous
            }
            primaryLabel={enrollment?.pauseWindow ? t`Save` : t`Pause`}
            primaryType="submit"
          />
        </Box>
      </form>
    </>
  );
}

function PauseInstructions() {
  const { t } = useTranslation(
    "client\\components\\account\\ParentPauseOngoingButton"
  );
  return (
    <>
      <Box sx={{ gap: "1em" }}>
        <Trans t={t}>
          When you pause, you will be temporarily unsubscribed from this class
          and may lose your seat. If you want to continue having access to
          recordings and materials, we recommend <b>not pausing</b> and letting
          your teacher know you’ll miss instead. If the class is full when your
          pause ends, you will be automatically added to the waitlist.
        </Trans>
      </Box>
      <Box
        flex
        sx={{
          flexDirection: "column",
          justifyContent: "space-between",
          marginTop: "1em",
          gap: "0.5em"
        }}
      >
        <span>
          <Icon icon={faPiggyBank} /> {t("No payments while absent")}
        </span>
        <span>
          <Icon icon={faDiamondExclamation} /> {t("Seat is not reserved")}
        </span>
        <span>
          <Icon icon={faUsersClass} /> {t("No access to classroom")}
        </span>
      </Box>
    </>
  );
}

interface PauseWeekDateTableProps {
  meetingsPerWeekPeriod: WeekPeriod[];
  isWeekSelectionContinuous: boolean;
  selectedWeeks: number[];
  setSelectedWeeks: React.Dispatch<React.SetStateAction<number[]>>;
  timeZone: string;
  disableThisWeek: boolean;
}

function PauseWeekDateTable({
  meetingsPerWeekPeriod,
  isWeekSelectionContinuous,
  disableThisWeek,
  selectedWeeks,
  setSelectedWeeks,
  timeZone
}: PauseWeekDateTableProps) {
  const { t } = useTranslation(
    "client\\components\\account\\ParentPauseOngoingButton"
  );

  const startOfThisWeek = dayjs().utc().startOf("isoWeek").toDate().getTime();
  return (
    <>
      <TableContainer
        sx={{
          display: "flex",
          padding: "20px",
          paddingBottom: 0,
          height: "250px",
          gap: "3rem",
          overflow: "scroll"
        }}
        component={Box}
      >
        <Table>
          <TableBody>
            {meetingsPerWeekPeriod.map(period => {
              const weekStartTime = dayjs(period.weekStart.getTime())
                .utc()
                .startOf("isoWeek")
                .valueOf();
              const disabled =
                disableThisWeek && weekStartTime === startOfThisWeek;

              return (
                <TableRow key={weekStartTime}>
                  <TableCell>
                    <Checkbox
                      iconSize="1em"
                      value={weekStartTime.toString()}
                      id={weekStartTime.toString()}
                      checked={!!selectedWeeks.find(w => w === weekStartTime)}
                      onChange={e => {
                        const checked = e.target.checked;
                        setSelectedWeeks(prevWeeks => {
                          return checked
                            ? [...prevWeeks, weekStartTime]
                            : _.without(prevWeeks, weekStartTime);
                        });
                      }}
                      sx={{ marginRight: "small" }}
                      disabled={disabled}
                    />
                  </TableCell>
                  {period.meetingsInPeriod.length === 0 ? (
                    <PauseNoMeetingRow
                      weekStart={period.weekStart}
                      weekEnd={period.weekEnd}
                      timeZone={timeZone}
                    />
                  ) : (
                    <PauseWeekMeetingRow
                      meetingsInPeriod={period.meetingsInPeriod}
                      timeZone={timeZone}
                    />
                  )}
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
      <ErrorMessage
        value={
          !isWeekSelectionContinuous && t("You must choose consecutive weeks.")
        }
      />
    </>
  );
}

function PauseWeekMeetingRow({ meetingsInPeriod, timeZone }: $TSFixMe) {
  return (
    <>
      <TableCell>
        {meetingsInPeriod.map((meetingTime: $TSFixMe, i: $TSFixMe) => (
          <Typography variant="inherit" sx={{ fontWeight: "500" }} key={i}>
            {i > 0 && <br />}
            {formatDateWithWeekday(meetingTime.start_time, timeZone)}
          </Typography>
        ))}
      </TableCell>
      <TableCell>
        {meetingsInPeriod.map((meetingTime: $TSFixMe, i: $TSFixMe) => (
          <React.Fragment key={i}>
            {i > 0 && <br />}
            {briefDurationTimesString(
              meetingTime.start_time,
              meetingTime.end_time,
              timeZone,
              false
            )}
          </React.Fragment>
        ))}
      </TableCell>
    </>
  );
}

function PauseNoMeetingRow({ weekStart, weekEnd, timeZone }: $TSFixMe) {
  const { t } = useTranslation(
    "client\\components\\account\\ParentPauseOngoingButton"
  );
  return (
    <>
      <TableCell>
        <Typography variant="inherit" sx={{ fontWeight: "500" }}>
          {briefDurationStringWithoutTimes(weekStart, weekEnd, timeZone)}
        </Typography>
      </TableCell>
      <TableCell>
        {dayjs().utc().isAfter(weekStart)
          ? t("No more meetings this week")
          : t("No meetings scheduled yet")}
      </TableCell>
    </>
  );
}

interface PausedSessionsSummaryProps {
  pausedMeetings: Meetings;
  isWeekSelectionContinuous: boolean;
  selectedWeeks: number[];
  timeZone: string;
}

function PausedSessionsSummary({
  pausedMeetings,
  isWeekSelectionContinuous,
  selectedWeeks,
  timeZone
}: PausedSessionsSummaryProps) {
  const { t } = useTranslation(
    "client\\components\\account\\ParentPauseOngoingButton"
  );
  return (
    <Box
      sx={{
        paddingBottom: "0.5em",
        borderBottom: "2px solid",
        borderColor: "grey.100",
        marginBottom: "1em"
      }}
    >
      <Typography variant="h6" sx={{ marginTop: "1em" }}>
        {t("Paused Sessions")}
      </Typography>
      <br />
      {selectedWeeks.length == 0 && (
        <Typography>{t("No weeks selected")}</Typography>
      )}
      {selectedWeeks.length > 0 && pausedMeetings.length == 0 && (
        <Typography>{t("No meetings scheduled during the pause")}</Typography>
      )}
      {pausedMeetings.map((meetingTime, i) => (
        <Typography
          key={i}
          sx={isWeekSelectionContinuous ? {} : { color: "error.main" }}
        >
          {i > 0 && ", "}
          {!!selectedWeeks.find(w => w === meetingTime.start_time)}
          {dayjs(meetingTime.start_time).tz(timeZone).format("MMMM D")}
        </Typography>
      ))}
    </Box>
  );
}

interface RulesArgs {
  meetings: Meetings;
  enrollment: EnrolledClassEnrollment | EnrolledClassGiftEnrollment;
}

function useDisplayRules({ meetings, enrollment }: RulesArgs) {
  const endOfThisWeek = useEndOfThisWeek();
  // Last meeting time is null if there are no more meetings this week.
  const lastMeetingTimeThisWeek = useLastMeetingTimeThisWeek(
    meetings,
    endOfThisWeek
  );
  // Pause can be null if in the past.
  const thisWeekPauseDeadline = useThisWeekPauseDeadline(enrollment);

  const isThisWeekDisabled =
    (!enrollment?.isPaused && !thisWeekPauseDeadline?.isAfter()) ||
    !lastMeetingTimeThisWeek;
  // Don't show an empty, disabled check box.
  const shouldStartAtNextWeek = isThisWeekDisabled && !enrollment?.isPaused;
  return { isThisWeekDisabled, shouldStartAtNextWeek };
}

function useEndOfThisWeek() {
  const [endOfThisWeek, setEndOfThisWeek] = React.useState(
    dayjs().utc().add(8, "day").startOf("isoWeek")
  );
  React.useEffect(() => {
    const nextUpdateTime = endOfThisWeek.toDate().getTime();
    const millisUntilNextUpdate = Math.max(
      nextUpdateTime - new Date().getTime(),
      1
    );
    const timeout = setTimeout(
      () => setEndOfThisWeek(dayjs().utc().add(8, "day").startOf("isoWeek")),
      millisUntilNextUpdate
    );

    // Clean up on unmount component.
    return () => clearTimeout(timeout);
  }, [endOfThisWeek]);

  return endOfThisWeek;
}

function getLastMeetingTimeThisWeek({
  meetings,
  endOfThisWeek
}: {
  meetings: Meetings;
  endOfThisWeek: dayjs.Dayjs;
}): dayjs.Dayjs | null {
  const now = new Date();
  const meetingsRemainingThisWeek = meetings.filter(m =>
    dayjs(m.start_time).isBetween(now, endOfThisWeek, "ms", "[)")
  );
  if (meetingsRemainingThisWeek.length === 0) {
    return null;
  }

  const lastMeetingThisWeek = _.orderBy(
    meetingsRemainingThisWeek,
    ["start_time"],
    ["desc"]
  )[0];
  return dayjs(lastMeetingThisWeek.start_time);
}

function useTransformedValueWithUpdates<T, TArgs>(
  getValue: (args: TArgs) => T,
  isEqual: (a: T, b: T) => boolean,
  args: TArgs
) {
  const [value, setValue] = React.useState(getValue(args));

  const refresh = React.useCallback(() => {
    const newValue = getValue(args);
    if (isEqual(newValue, value)) {
      return;
    }
    setValue(newValue);
  }, [getValue, args, isEqual, value]);

  React.useEffect(() => {
    refresh();
  }, [refresh]);
  return [value, refresh] as const;
}

function useLastMeetingTimeThisWeek(
  meetings: Meetings,
  endOfThisWeek: dayjs.Dayjs
) {
  const args = React.useMemo(
    () => ({ meetings, endOfThisWeek }),
    [meetings, endOfThisWeek]
  );
  const [lastMeetingTimeThisWeek, refresh] = useTransformedValueWithUpdates(
    getLastMeetingTimeThisWeek,
    _.isEqual,
    args
  );

  React.useEffect(() => {
    if (!lastMeetingTimeThisWeek) {
      return () => undefined;
    }

    const nextUpdateTime = lastMeetingTimeThisWeek.toDate().getTime();
    const millisUntilNextUpdate = Math.max(
      nextUpdateTime - new Date().getTime(),
      1
    );
    const timeout = setTimeout(refresh, millisUntilNextUpdate);
    // Clean up on unmount component.
    return () => clearTimeout(timeout);
  }, [lastMeetingTimeThisWeek, refresh]);

  return lastMeetingTimeThisWeek;
}

function getThisWeekPauseDeadline(
  enrollment: EnrolledClassEnrollment | EnrolledClassGiftEnrollment
): dayjs.Dayjs | null {
  const thisWeekPauseDeadline =
    enrollment?.thisWeekPauseDeadline &&
    dayjs(enrollment.thisWeekPauseDeadline);
  // Use this "double negative" structure because value can be null when in the past.
  if (!thisWeekPauseDeadline?.isAfter()) {
    return null;
  }
  return thisWeekPauseDeadline;
}

function useThisWeekPauseDeadline(
  enrollment: EnrolledClassEnrollment | EnrolledClassGiftEnrollment
) {
  const [thisWeekPauseDeadline, setThisWeekPauseDeadline] = React.useState(
    getThisWeekPauseDeadline(enrollment)
  );
  React.useEffect(() => {
    if (!thisWeekPauseDeadline) {
      return () => undefined;
    }

    const nextUpdateTime = thisWeekPauseDeadline.toDate().getTime();
    const millisUntilNextUpdate = Math.max(
      nextUpdateTime - new Date().getTime(),
      1
    );
    const timeout = setTimeout(
      () => setThisWeekPauseDeadline(getThisWeekPauseDeadline(enrollment)),
      millisUntilNextUpdate
    );
    // Clean up on unmount component.
    return () => clearTimeout(timeout);
  }, [enrollment, thisWeekPauseDeadline]);

  return thisWeekPauseDeadline;
}

function getFutureMeetings(meetings: Meetings) {
  const sorted = _.orderBy(meetings, "start_time", "asc");
  const nextMeetingIdx = sorted.findIndex(m => dayjs(m.start_time).isAfter());
  if (sorted.length === 0 || nextMeetingIdx === -1) {
    return [];
  }
  return sorted.slice(nextMeetingIdx);
}

function useFutureMeetings(meetings: Meetings) {
  const [futureMeetings, refresh] = useTransformedValueWithUpdates<
    Meetings,
    Meetings
  >(getFutureMeetings, _.isEqual, meetings);

  React.useEffect(() => {
    if (futureMeetings.length === 0) {
      return () => undefined;
    }

    const nextUpdateTime = dayjs(futureMeetings[0].start_time)
      .toDate()
      .getTime();
    const millisUntilNextUpdate = Math.max(
      nextUpdateTime - new Date().getTime(),
      1
    );
    const timeout = setTimeout(refresh, millisUntilNextUpdate);
    // Clean up on unmount component.
    return () => clearTimeout(timeout);
  }, [futureMeetings, refresh]);

  return futureMeetings;
}

function periodToWeeks(startDate: dayjs.Dayjs, endDate: dayjs.Dayjs) {
  // Convert to UTC to avoid DST issues when adding time periods.
  let date = dayjs(startDate).utc().startOf("isoWeek");
  const weeks: number[] = [];
  while (date.isBefore(endDate)) {
    weeks.push(date.valueOf());
    date = date.add(8, "day").startOf("isoWeek");
  }
  return weeks;
}

function weeksToPeriod(weekStartTimes: number[]): {
  pauseStartUtc: dayjs.Dayjs | null;
  pauseEndUtc: dayjs.Dayjs | null;
} {
  if (weekStartTimes.length === 0) {
    return { pauseStartUtc: null, pauseEndUtc: null };
  }
  const sortedWeeks = weekStartTimes.sort();
  const firstWeekStartMillis = sortedWeeks[0];
  // Convert to UTC to avoid 1hr offsets when crossing DST.
  const pauseStartUtc = dayjs(firstWeekStartMillis).utc();
  const lastWeekStartMillis = sortedWeeks[sortedWeeks.length - 1];
  const pauseEndUtc = dayjs(lastWeekStartMillis)
    .utc()
    .add(8, "day")
    .startOf("isoWeek");
  return { pauseStartUtc, pauseEndUtc };
}
